import React from "react";
import { FIRM } from "../lib/firm";

const money = (n) => "USD " + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const longDate = (iso) => {
  if (!iso) return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const d = (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso)) ? new Date(iso + "T00:00:00") : new Date(iso);
  if (isNaN(d)) return String(iso || "");
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const GREEN = "#123A2C";
const GOLD = "#C9A227";

const Shield = ({ size = 58 }) => (
  <div style={{ width: size, height: size * 1.12, position: "relative", flexShrink: 0 }}>
    <div style={{
      width: "100%", height: "100%",
      background: `linear-gradient(160deg, ${GREEN} 0%, #0C271D 100%)`,
      border: `2px solid ${GOLD}`, borderRadius: "6px 6px 40% 40% / 6px 6px 30% 30%",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "inset 0 0 0 3px rgba(201,162,39,0.25)",
    }}>
      <span style={{ fontFamily: "Georgia, serif", fontWeight: 800, fontSize: size * 0.62, color: GOLD, lineHeight: 1 }}>S</span>
    </div>
  </div>
);

/**
 * Mortgage Payoff & Closing Package Transmittal — the cover letter issued at the
 * front of every closing binder. Data is pulled from the application/binder.
 */
export default function PayoffTransmittal({
  borrowerName, obligationType, debtType, property, currentLender, accountNo,
  payoffAmount, ratePct, termMonths, monthlyPmt, financier, closingDate,
  packageId, mailDate, mortgages, uspsTracking, signatureUrl,
}) {
  const mail = longDate(mailDate || new Date().toISOString());
  const cell = { border: "1px solid #d9d9cf", padding: "7px 10px", fontSize: 11.5, verticalAlign: "top", lineHeight: 1.35 };
  const keyCell = { ...cell, background: "#f5f5ef", fontWeight: 600, width: "38%", color: "#2b2b25" };
  const valCell = { ...cell, color: "#1a1a14" };
  const hdrBar = { background: GREEN, color: "#fff", fontFamily: "Georgia, serif", fontWeight: 700, letterSpacing: "0.04em", fontSize: 12, textAlign: "center", padding: "7px 8px" };
  const _dt = (debtType || "").toLowerCase();
  const _NOUN = { mortgage: "Mortgage", auto: "Auto Loan", auto_loan: "Auto Loan", student_loan: "Student Loan", student: "Student Loan", credit_card: "Credit Card", personal: "Personal/Consumer Loan", consumer: "Personal/Consumer Loan", other: "Consumer Loan" };
  const isMortgage = _dt ? _dt === "mortgage" : /mortgage/i.test(obligationType || "");
  const noun = _NOUN[_dt] || (obligationType || "Loan");
  const titleWord = isMortgage ? "MORTGAGE" : noun.toUpperCase();
  const team = isMortgage ? "SEUTRADE Mortgage Team" : "SEUTRADE Payoff Team";
  const bodyNoun = isMortgage ? "mortgage loan" : `${noun.toLowerCase()} account`;
  const releaseClause = isMortgage
    ? "and prepare the appropriate release and/or satisfaction of the Security Deed in accordance with applicable state and local recording requirements."
    : "and issue the appropriate payoff / satisfaction confirmation and release of any associated lien or security interest.";
  const paidOffLabel = isMortgage ? "Mortgages Being Paid Off" : "Accounts Being Paid Off";
  const enclosures = isMortgage
    ? ["Closing Package", "Payoff Funds", "Settlement Statement", "Security Deed", "Supporting Documentation"]
    : ["Closing Package", "Payoff Funds", "Settlement Statement", "Payoff Statement", "Supporting Documentation"];
  const liens = Array.isArray(mortgages) ? mortgages.filter((m) => m && (m.lender || m.balance)) : [];

  return (
    <div className="paper-sheet legal-page" data-testid="binder-payoff-transmittal">
      <div style={{ fontFamily: "Georgia, 'Times New Roman', serif", color: "#1a1a14" }}>
        {/* Header / letterhead */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, paddingBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Shield />
            <div>
              <div style={{ fontFamily: "Georgia, serif", fontWeight: 800, fontSize: 30, color: GREEN, letterSpacing: "0.01em", lineHeight: 1 }}>SEUTRADE</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
                <span style={{ height: 1, width: 22, background: GOLD }} />
                <span style={{ letterSpacing: "0.42em", fontSize: 13, color: GOLD, fontWeight: 700 }}>FINANCE</span>
                <span style={{ height: 1, width: 22, background: GOLD }} />
              </div>
              <div style={{ fontStyle: "italic", fontSize: 11, color: "#4a4a42", marginTop: 4 }}>Where Opportunity Meets Homeownership.</div>
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: 12, color: "#2b2b25", lineHeight: 1.5 }}>
            <div>{FIRM.addressLines[0]}</div>
            <div>{FIRM.addressLines[1]}</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: GREEN, marginTop: 6 }}>{FIRM.phone}</div>
          </div>
        </div>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${GREEN} 0%, ${GOLD} 50%, ${GREEN} 100%)`, borderRadius: 2 }} />

        {/* Title */}
        <h1 style={{ textAlign: "center", fontFamily: "Georgia, serif", fontWeight: 800, fontSize: 20, letterSpacing: "0.02em", color: "#141410", margin: "22px 0 18px" }}>
          {titleWord} PAYOFF &amp; CLOSING PACKAGE TRANSMITTAL
        </h1>

        {/* Two info tables */}
        <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
          <table style={{ width: "44%", borderCollapse: "collapse" }}>
            <thead><tr><th colSpan={2} style={hdrBar}>PACKAGE INFORMATION</th></tr></thead>
            <tbody>
              <tr><td style={keyCell}>Package ID:</td><td style={valCell}>STF-{packageId || "__________"}</td></tr>
              <tr><td style={keyCell}>Mail Date:</td><td style={valCell}>{mail}</td></tr>
              <tr><td style={keyCell}>USPS Priority 2-Day Tracking Number:</td><td style={valCell} data-testid="transmittal-usps-tracking">{uspsTracking ? uspsTracking : "\u00A0"}</td></tr>
              <tr><td style={keyCell}>Processed By:</td><td style={valCell}>{team}</td></tr>
            </tbody>
          </table>
          <table style={{ width: "56%", borderCollapse: "collapse" }}>
            <thead><tr><th colSpan={2} style={hdrBar}>BORROWER / LOAN INFORMATION</th></tr></thead>
            <tbody>
              <tr><td style={keyCell}>Borrower / Obligor</td><td style={valCell}>{borrowerName || "—"}</td></tr>
              <tr><td style={keyCell}>Obligation Type</td><td style={valCell}>{obligationType || "—"}</td></tr>
              <tr><td style={keyCell}>Property</td><td style={valCell}>{property || "—"}</td></tr>
              {liens.length > 1 ? liens.map((m, i) => (
                <tr key={i}><td style={keyCell}>{i === 0 ? paidOffLabel : ""}</td>
                  <td style={valCell}>{m.position}: {m.lender || "—"}{m.account ? ` · Acct ${m.account}` : ""} · {money(m.balance)}</td></tr>
              )) : (
                <tr><td style={keyCell}>Current Lender / Servicer</td><td style={valCell}>{currentLender || "—"}{accountNo ? ` · Acct ${accountNo}` : ""}</td></tr>
              )}
              <tr><td style={keyCell}>{liens.length > 1 ? "Total Payoff / Financed" : "Payoff / Financed Amount"}</td><td style={valCell}>{money(payoffAmount)}</td></tr>
              <tr><td style={keyCell}>New Terms</td><td style={valCell}>{Number(ratePct || 0).toFixed(2)}% / yr · {termMonths || "—"} months · est. pmt {money(monthlyPmt)}</td></tr>
              <tr><td style={keyCell}>Financier</td><td style={valCell}>{financier || FIRM.name}</td></tr>
              <tr><td style={keyCell}>Closing Date</td><td style={valCell}>{longDate(closingDate)}</td></tr>
            </tbody>
          </table>
        </div>

        {/* Body */}
        <div style={{ fontSize: 12.5, lineHeight: 1.65, marginTop: 24, color: "#22221c" }}>
          <p style={{ margin: "0 0 14px" }}>Dear Payoff Department,</p>
          <p style={{ margin: "0 0 14px" }}>Enclosed please find the closing package for the above-referenced {bodyNoun}, which has been refinanced through <strong>SEUTRADE FINANCIAL SERVICES</strong>. This package is submitted for processing in accordance with the enclosed payoff documentation and closing instructions.</p>
          <p style={{ margin: "0 0 14px" }}>Upon receipt, we respectfully request that your office process the enclosed documents, apply the payoff funds to the above-referenced {isMortgage ? "loan" : "account"}, satisfy the indebtedness in full, {releaseClause}</p>
          <p style={{ margin: "0 0 14px" }}>Should additional documentation or information be required to complete the payoff process, please contact our office immediately so we may respond without delay.</p>
          <p style={{ margin: "0 0 14px" }}>Thank you for your prompt attention and cooperation. We appreciate your assistance in facilitating a smooth and timely loan payoff.</p>
        </div>

        {/* Signature + enclosures */}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 24, marginTop: 18 }}>
          <div style={{ fontSize: 12, lineHeight: 1.7 }}>
            <div>Warm Regards,</div>
            {signatureUrl ? (
              <img src={signatureUrl} alt="Authorized signature" data-testid="transmittal-signature-img"
                style={{ height: 46, maxWidth: 240, objectFit: "contain", display: "block", margin: "2px 0" }} />
            ) : null}
            <div style={{ fontWeight: 700 }}>{team}</div>
            <div>📞 {FIRM.phone}</div>
            <div>✉ seutradeinfo@gmail.com</div>
            <div style={{ fontWeight: 700, marginTop: 6 }}>Prepared by {team}</div>
            <div>{mail}</div>
          </div>
          <div style={{ width: 280 }}>
            <div style={{ fontFamily: "Georgia, serif", fontWeight: 700, letterSpacing: "0.06em", fontSize: 13, color: "#141410", borderBottom: `2px solid ${GOLD}`, paddingBottom: 4, marginBottom: 6 }}>ENCLOSURES</div>
            {enclosures.map((e) => (
              <div key={e} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "3px 0" }}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 15, height: 15, border: `1.5px solid ${GREEN}`, color: GREEN, fontSize: 11, lineHeight: 1, borderRadius: 2 }}>✓</span>
                <span>{e}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer band */}
        <div style={{ marginTop: 26, background: `linear-gradient(90deg, #0C271D, ${GREEN})`, borderTop: `2px solid ${GOLD}`, borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 16, padding: "12px 10px", position: "relative" }}>
          <span style={{ color: "#EDE7D2", letterSpacing: "0.14em", fontSize: 11.5, fontWeight: 600 }}>INTEGRITY</span>
          <span style={{ color: GOLD }}>|</span>
          <span style={{ color: "#EDE7D2", letterSpacing: "0.14em", fontSize: 11.5, fontWeight: 600 }}>TRUST</span>
          <span style={{ color: GOLD, fontWeight: 800 }}>◆</span>
          <span style={{ color: "#EDE7D2", letterSpacing: "0.14em", fontSize: 11.5, fontWeight: 600 }}>SOLUTIONS</span>
          <span style={{ color: GOLD }}>|</span>
          <span style={{ color: "#EDE7D2", letterSpacing: "0.14em", fontSize: 11.5, fontWeight: 600 }}>RESULTS</span>
        </div>
      </div>
    </div>
  );
}
